import { useState } from "react";
import type { InputParam, SortOrder } from "../../types/grid.ts";
import { ParamType } from "../../types/grid.ts";
import type { ColumnDescription } from "../../types/session.ts";
import { useToast } from "../../hooks/useToast.ts";
import { GROUP_BY_LABELS } from "./advancedFilters/groupByFilter.tsx";
import { describeViewSort } from "./savedViewSort.ts";
import styles from "./savedFiltersPanel.module.scss";

export interface SavedFilter {
  id: string;
  name: string;
  filters: InputParam[];
  paramFilters: InputParam[];
  isDefault: boolean;
  /** When true, date range params are stored as day-offsets from today and resolved at load time. */
  relativeDates?: boolean;
  /** Custom column selection active when the view was saved; null/absent = report default. */
  columns?: string[] | null;
  /** Column sort active when the view was saved; [] = unsorted, absent = saved before views kept
   *  the sort (leaves the current sort alone). See savedViewSort.ts. */
  sorts?: SortOrder[];
}

const STORAGE_PREFIX = "webadmincc-savedfilters-";
const OLD_STORAGE_PREFIX = "webadmincc-defaultfilter-";

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return String(Date.now()) + Math.random().toString(36).slice(2, 8);
}

/** Load saved filters from localStorage, migrating old format if needed. */
export function loadSavedFilters(gridName: string): SavedFilter[] {
  const key = STORAGE_PREFIX + gridName.toUpperCase();
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as SavedFilter[];
  } catch { /* ignore */ }

  // Migrate old single-filter format
  const oldKey = OLD_STORAGE_PREFIX + gridName.toUpperCase();
  try {
    const oldRaw = localStorage.getItem(oldKey);
    if (oldRaw) {
      const old = JSON.parse(oldRaw) as { filters?: InputParam[]; paramFilters?: InputParam[] };
      const migrated: SavedFilter[] = [{
        id: generateId(),
        name: "Default",
        filters: old.filters ?? [],
        paramFilters: old.paramFilters ?? [],
        isDefault: true,
      }];
      localStorage.setItem(key, JSON.stringify(migrated));
      localStorage.removeItem(oldKey);
      return migrated;
    }
  } catch { /* ignore */ }

  return [];
}

/** Persist saved filters to localStorage. */
export function persistSavedFilters(gridName: string, filters: SavedFilter[]): void {
  const key = STORAGE_PREFIX + gridName.toUpperCase();
  localStorage.setItem(key, JSON.stringify(filters));
}

/** Extract just the date portion from "YYYY-MM-DD HH:mm" backend strings. */
function dateOnly(val: string): string {
  if (!val) return "";
  return val.replace(/\s+\d{2}:\d{2}.*$/, "");
}

/** Check if an sColumn name represents a date range suffix (ends in 0 or 1, base not a real column). */
function isDateRangeSuffix(sColumn: string, colInfoMap: Record<string, ColumnDescription>): boolean {
  const raw = sColumn.replace(/^@/, "");
  if (!/[01]$/.test(raw)) return false;
  return !colInfoMap[raw.toLowerCase()];
}

/** Compute day offset from today for a date string. Negative = past, positive = future. */
function dateToDayOffset(dateStr: string): number {
  const d = dateOnly(dateStr);
  if (!d) return 0;
  const target = new Date(d + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function formatOffset(days: number): string {
  if (days === 0) return "today";
  if (days === -1) return "yesterday";
  if (days === 1) return "tomorrow";
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}

/** Convert a SavedFilter's date params to relative offsets stored as "REL:n" in oValue. */
function toRelativeFilter(filter: SavedFilter, colInfoMap: Record<string, ColumnDescription>): SavedFilter {
  const convertParams = (params: InputParam[]): InputParam[] =>
    params.map(p => {
      if (p.Type === ParamType.Unused) return p;
      if (!isDateRangeSuffix(p.sColumn, colInfoMap)) return p;
      const val = p.oValue == null ? "" : String(p.oValue);
      if (!val || val.startsWith("REL:")) return p;
      const offset = dateToDayOffset(val);
      const isEnd = p.sColumn.replace(/^@/, "").endsWith("1");
      return { ...p, oValue: `REL:${offset}:${isEnd ? "end" : "start"}` };
    });
  return { ...filter, relativeDates: true, paramFilters: convertParams(filter.paramFilters) };
}

/** Convert a SavedFilter's relative "REL:n" params back to absolute dates for loading. */
export function resolveRelativeDates(filter: SavedFilter): SavedFilter {
  if (!filter.relativeDates) return filter;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resolveParams = (params: InputParam[]): InputParam[] =>
    params.map(p => {
      const val = p.oValue == null ? "" : String(p.oValue);
      const m = val.match(/^REL:(-?\d+):(start|end)$/);
      if (!m) return p;
      const offset = parseInt(m[1]!, 10);
      const isEnd = m[2] === "end";
      const d = new Date(today);
      d.setDate(d.getDate() + offset);
      d.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, 0, 0);
      const yyyy = d.getFullYear();
      const MM = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const HH = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return { ...p, oValue: `${yyyy}-${MM}-${dd} ${HH}:${mm}` };
    });
  return { ...filter, paramFilters: resolveParams(filter.paramFilters) };
}

/** Check if a saved filter has any date range params that could be made relative. */
function hasDateParams(filter: SavedFilter, colInfoMap: Record<string, ColumnDescription>): boolean {
  return filter.paramFilters.some(p =>
    p.Type !== ParamType.Unused && isDateRangeSuffix(p.sColumn, colInfoMap),
  );
}

interface CommonOption {
  ColumnID?: string;
  Value?: unknown;
  Title?: string;
  sDisplayText?: string;
  sFilterValue?: string;
}

/** Resolve a combobox-style value (enum code, lookup id) to its display label
 * via the Common cache. Returns the raw value stringified when no match. */
function resolveComboLabel(
  rawValue: unknown,
  info: ColumnDescription | undefined,
  commonData: CommonOption[] | undefined,
): string {
  const str = rawValue == null ? "" : String(rawValue);
  if (!commonData || !info) return str;
  const lookupKey = (info.ColumnFormat || info.ColumnName || "").toLowerCase();
  if (!lookupKey) return str;
  const match = commonData.find((c) =>
    (c.ColumnID ?? "").toLowerCase() === lookupKey &&
    String(c.Value ?? c.sDisplayText ?? c.sFilterValue ?? "") === str,
  );
  return match?.Title ?? match?.sFilterValue ?? match?.sDisplayText ?? str;
}

/** Best-effort human-readable value for a filter chip. Strips LIKE wildcards,
 * localizes dates, resolves combo codes to labels, joins In lists. */
function formatChipValue(
  p: InputParam,
  info: ColumnDescription | undefined,
  commonData: CommonOption[] | undefined,
): string {
  const filterType = (info?.FilterType ?? "").toLowerCase();
  const raw = p.oValue == null ? "" : String(p.oValue);

  if (p.Type === ParamType.Like && raw.includes("%")) {
    const stripped = raw.replace(/%/g, "");
    if (raw.startsWith("%") && raw.endsWith("%")) return `contains ${stripped}`;
    if (raw.startsWith("%")) return `ends with ${stripped}`;
    if (raw.endsWith("%"))   return `starts with ${stripped}`;
    return stripped;
  }

  if (p.Type === ParamType.In && raw) {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return parts.map((v) => resolveComboLabel(v, info, commonData)).join(", ");
  }

  // Combo-ish filters: resolve enum/lookup code to label
  if (filterType.includes("combobox") || filterType.includes("checkcombobox") || filterType.includes("ticketstatus")) {
    return resolveComboLabel(raw, info, commonData);
  }

  // ISO-ish date: trim time / format to locale date
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return dateOnly(raw);

  return raw;
}

const TYPE_LABELS: Partial<Record<ParamType, string>> = {
  [ParamType.Equal]: "=",
  [ParamType.EqualOrLargerThen]: ">=",
  [ParamType.LessThen]: "<",
  [ParamType.LargerThen]: ">",
  [ParamType.Like]: "like",
  [ParamType.In]: "in",
  [ParamType.NotEqual]: "!=",
  [ParamType.IsNull]: "is null",
  [ParamType.IsEmpty]: "is empty",
};

/** Strip trailing 0/1 suffix if the suffixed name isn't a known column but the base name is. */
function resolveLabel(
  sColumn: string,
  displayNames: Record<string, string>,
  colInfoMap: Record<string, ColumnDescription>,
): string {
  const raw = sColumn.replace(/^@/, "");
  const lc = raw.toLowerCase();

  // Direct match — the suffixed name is a real column
  const info = colInfoMap[lc];
  if (info) return info.FilterText || info.Header || info.ColumnName;
  if (displayNames[lc]) return displayNames[lc];

  // Try stripping trailing 0/1 to find the base column
  if (/[01]$/.test(raw)) {
    const baseLc = lc.slice(0, -1);
    const baseInfo = colInfoMap[baseLc];
    if (baseInfo) return baseInfo.FilterText || baseInfo.Header || baseInfo.ColumnName;
    if (displayNames[baseLc]) return displayNames[baseLc];
  }

  return raw;
}

export interface DisplayLine { label: string; detail: string; sourceIndices: number[] }

/** Format a date value for display — strip time, or show relative offset. */
function displayDateVal(val: string): string {
  if (!val) return "";
  const relMatch = val.match(/^REL:(-?\d+):(start|end)$/);
  if (relMatch) return formatOffset(parseInt(relMatch[1]!, 10));
  return dateOnly(val);
}

/** Group range pairs (col0 + col1) into single lines, leave others as-is. */
export function buildDisplayLines(
  params: InputParam[],
  displayNames: Record<string, string>,
  colInfoMap: Record<string, ColumnDescription>,
  commonData?: CommonOption[],
): DisplayLine[] {
  const lines: DisplayLine[] = [];
  const used = new Set<number>();

  // For FilterGroupBy chips: the value of sGroupValue depends on which
  // sGroupBy column was picked (e.g. iOwnerOperatorID → lookup via Common).
  const activeGroupById = params.find(
    (p) => p.sColumn.replace(/^@/, "") === "sGroupBy" && p.Type !== ParamType.Unused,
  )?.oValue;
  const activeGroupKey = activeGroupById != null ? String(activeGroupById) : null;

  for (let i = 0; i < params.length; i++) {
    if (used.has(i)) continue;
    const p = params[i]!;
    const raw = p.sColumn.replace(/^@/, "");

    // Check for range pair: current ends with 0, look for matching 1
    if (/0$/.test(raw) && !colInfoMap[raw.toLowerCase()]) {
      const baseName = raw.slice(0, -1);
      const pairIdx = params.findIndex(
        (q, j) => j > i && !used.has(j) && q.sColumn.replace(/^@/, "") === baseName + "1",
      );
      if (pairIdx !== -1) {
        used.add(i);
        used.add(pairIdx);
        const pair = params[pairIdx]!;
        const label = resolveLabel(p.sColumn, displayNames, colInfoMap);
        const from = p.Type !== ParamType.Unused && p.oValue ? displayDateVal(String(p.oValue)) : "";
        const to = pair.Type !== ParamType.Unused && pair.oValue ? displayDateVal(String(pair.oValue)) : "";
        const sourceIndices = [i, pairIdx];
        if (from && to) lines.push({ label, detail: `${from}  \u2014  ${to}`, sourceIndices });
        else if (from) lines.push({ label, detail: `from ${from}`, sourceIndices });
        else if (to) lines.push({ label, detail: `to ${to}`, sourceIndices });
        continue;
      }
    }

    // Single param
    if (p.Type === ParamType.Unused) continue;
    const label = resolveLabel(p.sColumn, displayNames, colInfoMap);
    if (p.Type === ParamType.IsNull || p.Type === ParamType.IsEmpty) {
      lines.push({ label, detail: p.Type === ParamType.IsNull ? "is null" : "is empty", sourceIndices: [i] });
      continue;
    }
    const info = colInfoMap[p.sColumn.replace(/^@/, "").toLowerCase()];

    // FilterGroupBy special case: translate raw column id / operator id into
    // the label the user saw in the filter dropdowns.
    if (raw === "sGroupBy") {
      const idStr = p.oValue == null ? "" : String(p.oValue);
      lines.push({ label, detail: GROUP_BY_LABELS[idStr] ?? idStr, sourceIndices: [i] });
      continue;
    }
    if (raw === "sGroupValue") {
      const valStr = p.oValue == null ? "" : String(p.oValue);
      let detail = valStr;
      if (activeGroupKey === "iOwnerOperatorID") {
        // Null / 0 / empty → "Default"; otherwise resolve to Title via Common.
        if (!valStr || valStr === "0") {
          detail = "Default";
        } else if (commonData) {
          const match = (commonData as Array<{ ColumnName?: string; TypeName?: string; sName?: string; Value?: unknown; sFilterValue?: unknown; Title?: string; sDisplayText?: string }>)
            .find((c) => {
              const type = (c.ColumnName ?? c.TypeName ?? c.sName ?? "").toLowerCase();
              if (type !== "iowneroperatorid") return false;
              const id = c.Value != null ? String(c.Value) : c.sFilterValue != null ? String(c.sFilterValue) : "";
              return id === valStr;
            });
          const name = match?.Title ?? match?.sDisplayText;
          if (name) detail = name;
        }
      } else if (!valStr) {
        continue; // Non-operator group: empty value → hide the chip
      }
      lines.push({ label, detail, sourceIndices: [i] });
      continue;
    }

    const value = formatChipValue(p, info, commonData);
    // Drop the "=" prefix for the common Equal case; keep other operators explicit.
    const type = p.Type ?? ParamType.Equal;
    if (type === ParamType.Equal || type === ParamType.Like || type === ParamType.In) {
      lines.push({ label, detail: value, sourceIndices: [i] });
    } else {
      const op = TYPE_LABELS[type] ?? "=";
      lines.push({ label, detail: `${op} ${value}`, sourceIndices: [i] });
    }
  }

  return lines;
}

interface SavedFiltersPanelProps {
  gridName: string;
  currentFilters: InputParam[];
  currentParamFilters: InputParam[];
  /** Current custom column selection (null = report default) — stored with the view. */
  currentColumns?: string[] | null;
  /** Current column sort — stored with the view. */
  currentSorts: SortOrder[];
  savedFilters: SavedFilter[];
  displayNames: Record<string, string>;
  colInfoMap: Record<string, ColumnDescription>;
  onUpdate: (filters: SavedFilter[]) => void;
  onLoad: (filter: SavedFilter) => void;
}

export function SavedFiltersPanel({
  gridName,
  currentFilters,
  currentParamFilters,
  currentColumns,
  currentSorts,
  savedFilters,
  displayNames,
  colInfoMap,
  onUpdate,
  onLoad,
}: SavedFiltersPanelProps) {
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const hasDefault = savedFilters.some(f => f.isDefault);

  const handleSave = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const existing = savedFilters.find(f => f.name.toLowerCase() === trimmed.toLowerCase());
    let entry: SavedFilter;
    if (existing) {
      entry = { ...existing, filters: currentFilters, paramFilters: currentParamFilters, columns: currentColumns ?? null, sorts: currentSorts };
    } else {
      entry = {
        id: generateId(),
        name: trimmed,
        filters: currentFilters,
        paramFilters: currentParamFilters,
        isDefault: false,
        columns: currentColumns ?? null,
        sorts: currentSorts,
      };
    }
    // Default to relative dates if the filter has date params
    if (hasDateParams(entry, colInfoMap)) {
      entry = toRelativeFilter(entry, colInfoMap);
    }
    const updated = existing
      ? savedFilters.map(f => f.id === existing.id ? entry : f)
      : [...savedFilters, entry];
    persistSavedFilters(gridName, updated);
    onUpdate(updated);
    setNewName("");
    toast(existing ? `Filter "${trimmed}" updated` : `Filter "${trimmed}" saved`, "success");
  };

  const handleDelete = (id: string) => {
    const updated = savedFilters.filter(f => f.id !== id);
    persistSavedFilters(gridName, updated);
    onUpdate(updated);
  };

  const handleToggleDefault = (id: string) => {
    const updated = savedFilters.map(f => ({
      ...f,
      isDefault: f.id === id ? !f.isDefault : false,
    }));
    persistSavedFilters(gridName, updated);
    onUpdate(updated);
    const target = updated.find(f => f.id === id);
    toast(target?.isDefault ? `"${target.name}" set as default` : "Default cleared", "info");
  };

  const handleToggleRelative = (id: string) => {
    const target = savedFilters.find(f => f.id === id);
    if (!target) return;
    let replacement: SavedFilter;
    if (target.relativeDates) {
      // Resolve back to absolute (using today as reference)
      const resolved = resolveRelativeDates(target);
      replacement = { ...resolved, relativeDates: false };
    } else {
      replacement = toRelativeFilter(target, colInfoMap);
    }
    const updated = savedFilters.map(f => f.id === id ? replacement : f);
    persistSavedFilters(gridName, updated);
    onUpdate(updated);
    toast(replacement.relativeDates ? "Dates set to relative" : "Dates set to absolute", "info");
  };

  const handleClearDefault = () => {
    const updated = savedFilters.map(f => ({ ...f, isDefault: false }));
    persistSavedFilters(gridName, updated);
    onUpdate(updated);
    toast("Default filter cleared", "info");
  };

  return (
    <div className={styles.panel}>
      <div className={styles.saveRow}>
        <input
          className={styles.saveInput}
          type="text"
          placeholder="Save current as..."
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
        />
        <button className={styles.saveBtn} onClick={handleSave} disabled={!newName.trim()}>
          Save
        </button>
      </div>

      {savedFilters.length > 0 && (
        <div className={styles.list}>
          {savedFilters.map(f => {
            const isExpanded = expandedId === f.id;
            const allParams = [...f.filters, ...f.paramFilters];
            return (
              <div key={f.id} className={styles.itemWrap}>
                <div className={styles.item}>
                  <button
                    className={`${styles.starBtn}${f.isDefault ? ` ${styles.starActive}` : ""}`}
                    onClick={() => handleToggleDefault(f.id)}
                    title={f.isDefault ? "Remove as default" : "Set as default"}
                  >
                    {f.isDefault ? "\u2605" : "\u2606"}
                  </button>
                  <button
                    className={styles.filterName}
                    onClick={() => onLoad(f)}
                    title={`Load "${f.name}"`}
                  >
                    {f.name}
                  </button>
                  <button
                    className={`${styles.infoBtn}${isExpanded ? ` ${styles.infoBtnActive}` : ""}`}
                    onClick={() => setExpandedId(isExpanded ? null : f.id)}
                    title="View filter values"
                  >
                    &#x2139;
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(f.id)}
                    title="Delete"
                  >
                    &times;
                  </button>
                </div>
                {isExpanded && (() => {
                  const lines = buildDisplayLines(allParams, displayNames, colInfoMap);
                  const canToggleRelative = hasDateParams(f, colInfoMap);
                  const sortText = describeViewSort(f.sorts, displayNames);
                  return (
                    <div className={styles.detail}>
                      {sortText && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Sorted by</span>{" "}
                          <span className={styles.detailValue}>{sortText}</span>
                        </div>
                      )}
                      {lines.length === 0 ? (
                        <span className={styles.detailEmpty}>No filters stored</span>
                      ) : (
                        lines.map((line, i) => (
                          <div key={i} className={styles.detailRow}>
                            <span className={styles.detailLabel}>{line.label}</span>{" "}
                            <span className={styles.detailValue}>{line.detail}</span>
                          </div>
                        ))
                      )}
                      {canToggleRelative && (
                        <button
                          className={styles.relativeToggle}
                          onClick={() => handleToggleRelative(f.id)}
                        >
                          {f.relativeDates ? "Switch to absolute dates" : "Switch to relative dates"}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {hasDefault && (
        <div className={styles.footer}>
          <button className={styles.clearBtn} onClick={handleClearDefault}>
            Clear default
          </button>
        </div>
      )}
    </div>
  );
}
